import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Gift, Twitter, Facebook, Linkedin, Mail, Copy, Check, 
  Share2, Sparkles, BookOpen, Download, Award, ChevronRight,
  ExternalLink, Loader2, CheckCircle2, Image, Star, Crown,
  Palette, Wand2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../lib/api';

const CAT_GIFT = "https://static.prod-images.emergentagent.com/jobs/9146f3d9-a3b8-4907-acc0-5321df3c8e86/images/e0aebdb4136ccfbb07836d8a4918b929783a732c1a4be048d63bbb818f0ba07c.png";

// Book cover templates by genre - more vibrant and professional
const COVER_TEMPLATES = {
  fantasy: {
    bg: 'from-indigo-900 via-purple-900 to-slate-900',
    accent: 'text-cyan-400',
    border: 'border-cyan-500/20',
    pattern: 'radial-gradient(circle at 30% 20%, rgba(168, 85, 247, 0.4) 0%, transparent 50%)',
    gradientStops: ['#312e81', '#581c87', '#0f172a'],
    accentColor: 'rgba(255, 215, 0, 0.8)',
  },
  romance: {
    bg: 'from-rose-900 via-pink-900 to-slate-900',
    accent: 'text-rose-300',
    border: 'border-rose-500/30',
    pattern: 'radial-gradient(circle at 70% 80%, rgba(244, 114, 182, 0.3) 0%, transparent 50%)',
    gradientStops: ['#881337', '#831843', '#0f172a'],
    accentColor: 'rgba(251, 113, 133, 0.9)',
  },
  thriller: {
    bg: 'from-slate-900 via-red-950 to-black',
    accent: 'text-red-500',
    border: 'border-red-500/30',
    pattern: 'linear-gradient(135deg, rgba(220, 38, 38, 0.2) 0%, transparent 50%)',
    gradientStops: ['#0f172a', '#450a0a', '#000000'],
    accentColor: 'rgba(239, 68, 68, 0.9)',
  },
  scifi: {
    bg: 'from-cyan-900 via-slate-900 to-black',
    accent: 'text-cyan-400',
    border: 'border-cyan-500/30',
    pattern: 'radial-gradient(circle at 50% 0%, rgba(34, 211, 238, 0.3) 0%, transparent 60%)',
    gradientStops: ['#164e63', '#0f172a', '#000000'],
    accentColor: 'rgba(34, 211, 238, 0.9)',
  },
  literary: {
    bg: 'from-slate-800 via-slate-900 to-black',
    accent: 'text-cyan-200',
    border: 'border-cyan-500/25',
    pattern: 'none',
    gradientStops: ['#292524', '#1c1917', '#000000'],
    accentColor: 'rgba(253, 230, 138, 0.9)',
  },
  mystery: {
    bg: 'from-slate-900 via-emerald-950 to-black',
    accent: 'text-emerald-400',
    border: 'border-emerald-500/30',
    pattern: 'radial-gradient(circle at 50% 100%, rgba(16, 185, 129, 0.2) 0%, transparent 50%)',
    gradientStops: ['#0f172a', '#022c22', '#000000'],
    accentColor: 'rgba(52, 211, 153, 0.9)',
  },
  horror: {
    bg: 'from-black via-red-950 to-black',
    accent: 'text-red-600',
    border: 'border-red-900/50',
    pattern: 'radial-gradient(circle at 50% 50%, rgba(127, 29, 29, 0.3) 0%, transparent 70%)',
    gradientStops: ['#000000', '#450a0a', '#000000'],
    accentColor: 'rgba(185, 28, 28, 0.9)',
  },
  historical: {
    bg: 'from-slate-800 via-slate-900 to-black',
    accent: 'text-cyan-300',
    border: 'border-cyan-600/30',
    pattern: 'none',
    gradientStops: ['#78350f', '#1c1917', '#000000'],
    accentColor: 'rgba(252, 211, 77, 0.9)',
  },
  ya: {
    bg: 'from-violet-800 via-fuchsia-900 to-slate-900',
    accent: 'text-fuchsia-300',
    border: 'border-fuchsia-500/30',
    pattern: 'radial-gradient(circle at 80% 20%, rgba(232, 121, 249, 0.3) 0%, transparent 50%)',
    gradientStops: ['#5b21b6', '#701a75', '#0f172a'],
    accentColor: 'rgba(232, 121, 249, 0.9)',
  },
};

const SHARE_REWARDS = [
  {
    icon: Image,
    title: 'Shweebum Nova Book Cover',
    description: 'One FREE AI-generated professional book cover!',
    color: 'violet',
    premium: true
  },
  {
    icon: BookOpen,
    title: '+5 Extra Chapters',
    description: 'Unlock 20 chapters instead of 15',
    color: 'emerald'
  },
  {
    icon: Award,
    title: 'Ambassador Badge',
    description: 'Show off your supporter status',
    color: 'cyan'
  }
];

// Book Cover Preview Component
const BookCoverPreview = ({ title, authorName, genre = 'fantasy', onDownload }) => {
  const template = COVER_TEMPLATES[genre] || COVER_TEMPLATES.fantasy;
  const coverRef = React.useRef(null);

  const handleDownload = () => {
    // Create a canvas to render the cover
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 600;
    canvas.height = 900;

    // Draw gradient background
    const gradient = ctx.createLinearGradient(0, 0, 0, 900);
    if (genre === 'fantasy') {
      gradient.addColorStop(0, '#312e81');
      gradient.addColorStop(0.5, '#581c87');
      gradient.addColorStop(1, '#0f172a');
    } else if (genre === 'romance') {
      gradient.addColorStop(0, '#881337');
      gradient.addColorStop(0.5, '#831843');
      gradient.addColorStop(1, '#0f172a');
    } else if (genre === 'thriller') {
      gradient.addColorStop(0, '#0f172a');
      gradient.addColorStop(0.5, '#450a0a');
      gradient.addColorStop(1, '#000000');
    } else if (genre === 'scifi') {
      gradient.addColorStop(0, '#164e63');
      gradient.addColorStop(0.5, '#0f172a');
      gradient.addColorStop(1, '#000000');
    } else {
      gradient.addColorStop(0, '#292524');
      gradient.addColorStop(0.5, '#1c1917');
      gradient.addColorStop(1, '#000000');
    }
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 600, 900);

    // Draw decorative elements
    ctx.fillStyle = 'rgba(255, 255, 255, 0.03)';
    ctx.beginPath();
    ctx.arc(150, 150, 200, 0, Math.PI * 2);
    ctx.fill();

    // Draw border
    ctx.strokeStyle = 'rgba(255, 215, 0, 0.3)';
    ctx.lineWidth = 2;
    ctx.strokeRect(30, 30, 540, 840);

    // Draw title
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 48px Georgia, serif';
    ctx.textAlign = 'center';
    
    // Word wrap title
    const words = title.split(' ');
    let lines = [];
    let currentLine = '';
    words.forEach(word => {
      const testLine = currentLine + word + ' ';
      if (ctx.measureText(testLine).width > 480) {
        lines.push(currentLine.trim());
        currentLine = word + ' ';
      } else {
        currentLine = testLine;
      }
    });
    lines.push(currentLine.trim());

    // Draw title lines
    const startY = 350 - (lines.length * 30);
    lines.forEach((line, i) => {
      ctx.fillText(line, 300, startY + (i * 60));
    });

    // Draw "A Novel" text
    ctx.fillStyle = 'rgba(255, 215, 0, 0.8)';
    ctx.font = 'italic 24px Georgia, serif';
    ctx.fillText('A Novel', 300, startY + (lines.length * 60) + 40);

    // Draw author name
    ctx.fillStyle = '#ffffff';
    ctx.font = '32px Georgia, serif';
    ctx.fillText(authorName, 300, 780);

    // Draw decorative line
    ctx.strokeStyle = 'rgba(255, 215, 0, 0.5)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(150, 730);
    ctx.lineTo(450, 730);
    ctx.stroke();

    // Download
    const link = document.createElement('a');
    link.download = `${title.replace(/[^a-z0-9]/gi, '_')}_cover.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();

    toast.success('Book cover downloaded! Share it everywhere! 📚');
  };

  return (
    <div className="flex flex-col items-center">
      {/* Cover Preview */}
      <div 
        ref={coverRef}
        className={`relative w-48 h-72 rounded-lg shadow-2xl overflow-hidden bg-gradient-to-b ${template.bg} border ${template.border}`}
        style={{ 
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 30px rgba(168, 85, 247, 0.2)',
        }}
      >
        {/* Pattern overlay */}
        <div 
          className="absolute inset-0 opacity-50"
          style={{ background: template.pattern }}
        />
        
        {/* Border decoration */}
        <div className="absolute inset-3 border border-white/10 rounded" />
        
        {/* Content */}
        <div className="relative h-full flex flex-col items-center justify-center p-4 text-center">
          {/* Title */}
          <h3 className="text-white font-serif font-bold text-lg leading-tight mb-2 drop-shadow-lg">
            {title || 'Your Book Title'}
          </h3>
          
          {/* Subtitle */}
          <p className={`text-xs ${template.accent} italic mb-4`}>A Novel</p>
          
          {/* Decorative line */}
          <div className={`w-16 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent mb-4`} />
          
          {/* Author */}
          <p className="text-white/90 text-sm font-serif">
            {authorName || 'Your Name'}
          </p>
        </div>
        
        {/* Spine effect */}
        <div className="absolute left-0 top-0 bottom-0 w-2 bg-gradient-to-r from-black/40 to-transparent" />
      </div>

      {/* Download button */}
      <Button
        onClick={handleDownload}
        className="mt-4 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500"
      >
        <Download className="w-4 h-4 mr-2" />
        Download Cover
      </Button>
    </div>
  );
};

export const ShareRewardsModal = ({ open, onClose, user, projects = [], onRewardsClaimed }) => {
  const [step, setStep] = useState('main'); // 'main', 'share', 'claimed', 'cover-generator'
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [hasClaimedReward, setHasClaimedReward] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [customTitle, setCustomTitle] = useState('');
  const [customAuthor, setCustomAuthor] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('fantasy');
  const [coverDescription, setCoverDescription] = useState('');
  const [generatingAI, setGeneratingAI] = useState(false);
  const [aiCoverImage, setAiCoverImage] = useState(null);
  const [hasUsedFreeCover, setHasUsedFreeCover] = useState(false);

  useEffect(() => {
    if (open && user?.id) {
      checkRewardStatus();
      generateReferralCode();
      checkFreeCoverStatus();
    }
  }, [open, user?.id]);

  const checkFreeCoverStatus = async () => {
    try {
      const res = await axios.get(`${API}/free-cover-status/${user.id}`);
      setHasUsedFreeCover(res.data.has_used_free_cover);
      if (res.data.cover_data?.image_base64) {
        setAiCoverImage(res.data.cover_data.image_base64);
        setCustomTitle(res.data.cover_data.title || '');
        setCustomAuthor(res.data.cover_data.author_name || '');
      }
    } catch (err) {
      // No free cover used yet
      setHasUsedFreeCover(false);
    }
  };

  const checkRewardStatus = async () => {
    // Family accounts ALWAYS have rewards unlocked
    const isFamilyAccount = user?.subscription === 'family' || user?.is_family;
    if (isFamilyAccount) {
      setHasClaimedReward(true);
      setIsLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${API}/user/${user.id}/share-reward-status`);
      setHasClaimedReward(res.data.claimed);
    } catch (err) {
      // Fallback to localStorage if backend unavailable
      const claimed = localStorage.getItem(`shweebum_share_reward_${user?.id}`);
      setHasClaimedReward(claimed === 'true');
    }
    setIsLoading(false);
  };

  const generateReferralCode = () => {
    const code = `SHW-${user?.id?.slice(0, 8)?.toUpperCase() || 'GIFT'}`;
    setReferralCode(code);
    setReferralLink(`${window.location.origin}?ref=${code}`);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Link copied! Share it anywhere!');
    setTimeout(() => setCopied(false), 3000);
  };

  const handleShare = (platform) => {
    // Rotating share messages that feel personal and inviting
    const shareMessages = [
      "I'm finally writing my book! 📚 Shweebum is like having a creative partner who never runs out of ideas. Try it free:",
      "Got a story in your head? Turn it into a book! I'm using Shweebum and it's amazing. Free to start:",
      "Most people never finish their book. I will. 📖 Check out Shweebum - it writes WITH you:",
      "Writer's block? What's that? 😄 Shweebum helps me finish every chapter. Start your book free:"
    ];
    const text = shareMessages[Math.floor(Math.random() * shareMessages.length)];
    const url = encodeURIComponent(referralLink);

    let shareUrl = '';
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${url}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${encodeURIComponent(text)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      case 'email':
        const subject = encodeURIComponent('I found something amazing for writers!');
        const body = encodeURIComponent(
          `Hey!\n\nI've been using Shweebum to write my book and I had to share it with you!\n\nIt's like having a creative writing partner who helps you:\n- Plan your entire story\n- Develop characters\n- Overcome writer's block\n- Actually FINISH your book\n\nThe best part? It's free to start - no credit card needed.\n\nCheck it out: ${referralLink}\n\nHappy writing! 📚`
        );
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
        return;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=550,height=420');
    }

    // If not claimed yet, show claim option
    if (!hasClaimedReward) {
      setTimeout(() => {
        setStep('claim-confirm');
      }, 1000);
    }
  };

  const claimReward = async () => {
    try {
      // Persist to backend (MongoDB)
      await axios.post(`${API}/user/${user.id}/claim-share-reward`);
      
      // Also keep localStorage as quick-access cache
      localStorage.setItem(`shweebum_share_reward_${user?.id}`, 'true');
      localStorage.setItem(`shweebum_extra_chapters_${user?.id}`, '5');
      localStorage.setItem(`shweebum_ambassador_${user?.id}`, 'true');
      localStorage.setItem(`shweebum_cover_unlocked_${user?.id}`, 'true');
      
      setHasClaimedReward(true);
      setStep('claimed');
      toast.success('Rewards unlocked! You now have +5 chapters and the Book Cover Generator!');
      
      if (onRewardsClaimed) onRewardsClaimed();
    } catch (err) {
      // Still allow claim even if backend fails temporarily
      localStorage.setItem(`shweebum_share_reward_${user?.id}`, 'true');
      setHasClaimedReward(true);
      setStep('claimed');
      toast.success('Rewards unlocked!');
      if (onRewardsClaimed) onRewardsClaimed();
    }
  };

  const goToCoverGenerator = () => {
    // Pre-fill with first project if available
    if (projects.length > 0) {
      setSelectedProject(projects[0]);
      setCustomTitle(projects[0].title || '');
      setCustomAuthor(user?.name || '');
      setSelectedGenre(projects[0].genre?.toLowerCase() || 'fantasy');
    } else {
      setCustomTitle('');
      setCustomAuthor(user?.name || '');
    }
    setStep('cover-generator');
  };

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700">
          <div className="p-8 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-slate-900 border-slate-700/60 p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
        
        {step === 'main' && (
          <>
            {/* Header */}
            <div className="bg-gradient-to-r from-violet-600/70 via-pink-600/50 to-cyan-600/70 p-6 text-center">
              <div className="flex items-center justify-center gap-3 mb-3">
                <img src={CAT_GIFT} alt="Gift" className="w-16 h-16 object-contain drop-shadow-lg" />
                <Gift className="w-10 h-10 text-cyan-300 animate-pulse" />
              </div>
              <h2 className="text-2xl font-bold text-white">
                {hasClaimedReward ? 'Share Shweebum!' : 'Share & Get FREE Gifts!'}
              </h2>
              <p className="text-white/80 text-sm mt-1">
                {hasClaimedReward 
                  ? 'Help other writers discover Shweebum' 
                  : 'Share once, unlock rewards forever!'
                }
              </p>
            </div>

            {/* Rewards Preview */}
            {!hasClaimedReward && (
              <div className="p-4 border-b border-slate-700">
                <h3 className="text-sm font-semibold text-cyan-400 mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Share to Unlock These FREE Gifts:
                </h3>
                <div className="space-y-2">
                  {SHARE_REWARDS.map((reward, idx) => (
                    <div 
                      key={idx}
                      className={`flex items-center gap-3 p-3 rounded-lg bg-${reward.color}-500/10 border border-${reward.color}-500/20`}
                    >
                      <div className={`w-10 h-10 rounded-lg bg-${reward.color}-500/20 flex items-center justify-center`}>
                        <reward.icon className={`w-5 h-5 text-${reward.color}-400`} />
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium text-sm flex items-center gap-2">
                          {reward.title}
                          {reward.premium && (
                            <span className="text-[10px] bg-violet-500/30 text-violet-300 px-1.5 py-0.5 rounded">
                              ⭐ SPECIAL
                            </span>
                          )}
                        </p>
                        <p className="text-slate-400 text-xs">{reward.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Already Claimed - Show Access */}
            {hasClaimedReward && (
              <div className="p-4 border-b border-slate-700">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-sm font-semibold text-emerald-400">Rewards Unlocked!</h3>
                </div>
                <Button
                  onClick={goToCoverGenerator}
                  className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 mb-2"
                >
                  <Image className="w-4 h-4 mr-2" />
                  Open Shweebum Nova Cover
                </Button>
                <p className="text-xs text-slate-400 text-center">
                  You have +5 extra chapters & Ambassador badge active
                </p>
              </div>
            )}

            {/* Share Options */}
            <div className="p-4 space-y-2">
              <h3 className="text-sm font-semibold text-white mb-3">
                {hasClaimedReward ? 'Share with More Friends' : 'Choose How to Share:'}
              </h3>
              
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => handleShare('twitter')}
                  className="flex items-center gap-2 p-3 bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20 border border-[#1DA1F2]/30 rounded-lg transition-colors"
                >
                  <Twitter className="w-5 h-5 text-[#1DA1F2]" />
                  <span className="text-white text-sm">Twitter/X</span>
                </button>

                <button
                  onClick={() => handleShare('facebook')}
                  className="flex items-center gap-2 p-3 bg-[#4267B2]/10 hover:bg-[#4267B2]/20 border border-[#4267B2]/30 rounded-lg transition-colors"
                >
                  <Facebook className="w-5 h-5 text-[#4267B2]" />
                  <span className="text-white text-sm">Facebook</span>
                </button>

                <button
                  onClick={() => handleShare('linkedin')}
                  className="flex items-center gap-2 p-3 bg-[#0077B5]/10 hover:bg-[#0077B5]/20 border border-[#0077B5]/30 rounded-lg transition-colors"
                >
                  <Linkedin className="w-5 h-5 text-[#0077B5]" />
                  <span className="text-white text-sm">LinkedIn</span>
                </button>

                <button
                  onClick={() => handleShare('email')}
                  className="flex items-center gap-2 p-3 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-lg transition-colors"
                >
                  <Mail className="w-5 h-5 text-emerald-400" />
                  <span className="text-white text-sm">Email</span>
                </button>
              </div>

              {/* Copy Link */}
              <div className="mt-4 p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                <p className="text-xs text-slate-400 mb-2">Or copy your referral link:</p>
                <div className="flex gap-2">
                  <Input 
                    value={referralLink}
                    readOnly
                    className="bg-slate-900 border-slate-700 text-slate-300 text-sm"
                  />
                  <Button
                    onClick={copyToClipboard}
                    variant="outline"
                    className="border-slate-600 shrink-0"
                  >
                    {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}

        {step === 'claim-confirm' && (
          <div className="p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-cyan-500/20 to-violet-500/20 rounded-full flex items-center justify-center">
              <Gift className="w-8 h-8 text-cyan-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Thanks for Sharing! 🎉</h3>
            <p className="text-slate-400 text-sm mb-6">
              Click below to claim your free gifts. This is a one-time reward!
            </p>
            
            <div className="space-y-3 mb-6">
              {SHARE_REWARDS.map((reward, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 bg-slate-800/50 rounded-lg">
                  <reward.icon className={`w-5 h-5 text-${reward.color}-400`} />
                  <span className="text-white text-sm">{reward.title}</span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 ml-auto" />
                </div>
              ))}
            </div>

            <Button
              onClick={claimReward}
              className="w-full bg-gradient-to-r from-cyan-500 to-violet-500 hover:from-cyan-400 hover:to-violet-400 text-white font-semibold py-5"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Claim My Free Gifts!
            </Button>
            
            <button
              onClick={() => setStep('main')}
              className="mt-3 text-slate-400 text-sm hover:text-slate-300"
            >
              Maybe later
            </button>
          </div>
        )}

        {step === 'claimed' && (
          <div className="p-6 text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-emerald-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Rewards Unlocked! 🎉</h3>
            <p className="text-slate-400 text-sm mb-6">
              You now have access to all your free gifts!
            </p>
            
            <div className="space-y-3 mb-6">
              <div className="p-4 bg-violet-500/10 border border-violet-500/30 rounded-lg">
                <Image className="w-8 h-8 text-violet-400 mx-auto mb-2" />
                <p className="text-white font-medium">Shweebum Nova Book Cover</p>
                <p className="text-violet-300 text-xs">One FREE AI-generated professional cover!</p>
              </div>
              <div className="flex gap-3">
                <div className="flex-1 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-center">
                  <BookOpen className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                  <p className="text-white text-sm">+5 Chapters</p>
                </div>
                <div className="flex-1 p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg text-center">
                  <Award className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
                  <p className="text-white text-sm">Ambassador</p>
                </div>
              </div>
            </div>

            <Button
              onClick={goToCoverGenerator}
              className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              Create My Nova Cover!
            </Button>
            
            <button
              onClick={onClose}
              className="mt-3 text-slate-400 text-sm hover:text-slate-300"
            >
              Close
            </button>
          </div>
        )}

        {step === 'cover-generator' && (
          <div className="p-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-bold text-white flex items-center justify-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-400" />
                Shweebum Nova Book Cover
              </h3>
              <p className="text-slate-400 text-sm">
                {hasUsedFreeCover 
                  ? "You've used your free Nova cover. Upgrade for more!" 
                  : "Create ONE stunning AI-generated cover - FREE!"}
              </p>
            </div>

            {/* Show existing AI cover if already generated */}
            {aiCoverImage && (
              <div className="mb-6 text-center">
                <p className="text-emerald-400 text-sm mb-3 flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Your Shweebum Nova Cover
                </p>
                <img 
                  src={`data:image/png;base64,${aiCoverImage}`}
                  alt="Shweebum Nova Cover"
                  className="max-w-[200px] mx-auto rounded-lg shadow-2xl border border-violet-500/30"
                />
                <Button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.download = `${customTitle || 'cover'}_nova_cover.png`;
                    link.href = `data:image/png;base64,${aiCoverImage}`;
                    link.click();
                    toast.success('Nova cover downloaded!');
                  }}
                  className="mt-4 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Nova Cover
                </Button>
              </div>
            )}

            {/* Show form if no AI cover yet or show basic cover option */}
            {!aiCoverImage && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Book Title *</label>
                  <Input
                    value={customTitle}
                    onChange={(e) => setCustomTitle(e.target.value)}
                    placeholder="Enter your book title"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Author Name *</label>
                  <Input
                    value={customAuthor}
                    onChange={(e) => setCustomAuthor(e.target.value)}
                    placeholder="Your name"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>

                <div>
                  <label className="text-sm text-slate-400 mb-2 block">Genre</label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.keys(COVER_TEMPLATES).map((genre) => (
                      <button
                        key={genre}
                        onClick={() => setSelectedGenre(genre)}
                        className={`p-2 rounded-lg text-xs capitalize transition-all ${
                          selectedGenre === genre
                            ? 'bg-violet-500/30 border-violet-500 text-violet-300 border'
                            : 'bg-slate-800 border-slate-700 text-slate-400 border hover:border-slate-600'
                        }`}
                      >
                        {genre}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm text-slate-400 mb-1 block">
                    <Sparkles className="w-3 h-3 inline mr-1 text-violet-400" />
                    Describe Your Cover (Nova will create this!)
                  </label>
                  <textarea
                    value={coverDescription}
                    onChange={(e) => setCoverDescription(e.target.value)}
                    placeholder="Example: A mysterious hooded figure standing at the edge of a cliff overlooking a glowing fantasy city at sunset. Dragons flying in the distance..."
                    className="w-full bg-slate-800 border-slate-700 rounded-lg p-3 text-white text-sm min-h-[100px] resize-none"
                    maxLength={300}
                  />
                  <p className="text-xs text-slate-500 mt-1">{coverDescription.length}/300 characters</p>
                </div>

                {projects.length > 0 && (
                  <div>
                    <label className="text-sm text-slate-400 mb-2 block">Or select a project:</label>
                    <select
                      onChange={(e) => {
                        const proj = projects.find(p => p.id === e.target.value);
                        if (proj) {
                          setCustomTitle(proj.title || '');
                          setSelectedGenre(proj.genre?.toLowerCase() || 'fantasy');
                        }
                      }}
                      className="w-full bg-slate-800 border-slate-700 rounded-lg p-2 text-white text-sm"
                    >
                      <option value="">Choose a project...</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>{p.title}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Generate Nova Cover Button */}
                {!hasUsedFreeCover && (
                  <Button
                    onClick={async () => {
                      if (!customTitle || !customAuthor) {
                        toast.error('Please enter a title and author name');
                        return;
                      }
                      setGeneratingAI(true);
                      try {
                        const res = await axios.post(`${API}/generate-free-cover`, {
                          user_id: user.id,
                          title: customTitle,
                          author_name: customAuthor,
                          genre: selectedGenre,
                          description: coverDescription
                        }, { timeout: 150000 });
                        
                        if (res.data.success) {
                          setAiCoverImage(res.data.image_base64);
                          setHasUsedFreeCover(true);
                          toast.success('Your Shweebum Nova cover is ready! 🎨');
                        }
                      } catch (err) {
                        if (err.response?.status === 403) {
                          toast.error(err.response.data.detail);
                          setHasUsedFreeCover(true);
                        } else {
                          toast.error(err.response?.data?.detail || 'Failed to generate cover');
                        }
                      } finally {
                        setGeneratingAI(false);
                      }
                    }}
                    disabled={generatingAI || !customTitle || !customAuthor}
                    className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 py-4"
                  >
                    {generatingAI ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Nova is creating your cover (30-60 sec)...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-5 h-5 mr-2" />
                        Generate Nova Cover (1 Free!)
                      </>
                    )}
                  </Button>
                )}

                {hasUsedFreeCover && !aiCoverImage && (
                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg text-center">
                    <Crown className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                    <p className="text-amber-300 text-sm font-medium">You've used your free Nova cover</p>
                    <p className="text-slate-400 text-xs mt-1">Upgrade to Plus for 5 covers/month or Premium for unlimited!</p>
                    <Button
                      onClick={() => window.location.href = '/pricing'}
                      variant="outline"
                      className="mt-3 border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                      size="sm"
                    >
                      <Star className="w-4 h-4 mr-1" />
                      View Plans
                    </Button>
                  </div>
                )}

                {/* Divider */}
                <div className="flex items-center gap-3 my-4">
                  <div className="flex-1 h-px bg-slate-700"></div>
                  <span className="text-xs text-slate-500">or try basic cover</span>
                  <div className="flex-1 h-px bg-slate-700"></div>
                </div>

                {/* Basic Cover Preview */}
                <div className="flex flex-col items-center">
                  <p className="text-xs text-slate-500 mb-2">Basic Cover (No AI)</p>
                  <BookCoverPreview
                    title={customTitle || 'Your Book Title'}
                    authorName={customAuthor || 'Your Name'}
                    genre={selectedGenre}
                  />
                </div>
              </div>
            )}

            <div className="mt-6 flex gap-2">
              <Button
                variant="outline"
                onClick={() => setStep('main')}
                className="flex-1 border-slate-700"
              >
                Back
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareRewardsModal;
